import { useLazyQuery } from "@apollo/client";
import { ChangeEvent, InvalidEvent, useCallback, useEffect, useMemo } from "react";
import { FaTriangleExclamation } from "react-icons/fa6";
import { Col, FormFeedback, FormGroup, Input, Label, Row } from "reactstrap";
import { SEARCH_INDIVIDUALS_FOR_NEW_USER } from "../../../graphql/individual-gql";
import { GET_ORG_BY_POSITION } from "../../../graphql/position-gql";
import { ContainerLayoutProps, IOption, NamedEntity } from "../../../types/common";
import { ISearchIndividualsForNewUserResponse, ISearchIndividualsForNewResult } from "../../../types/individual";
import { Action } from "../../../types/reducer-action";
import { equals, trimToEmpty } from "../../../utils/commons";
import { DataEntryEvent, FormErrorAction, FormUpdateAction } from "../../common/data-entry-reducer";
import AnimatedPlaceholder from "../../common/animated-placeholder";
import InlineFeedback from "../../common/inline-feedback";
import LabelCol from "../../common/label-col";
import OmsToolTip from "../../common/oms-tooltip/oms-tooltip";
import SearchInput from "../../common/search-input/search-input";
import './style.scss';
import useUserApi from "./useUserApi";
import { UserProfileForm, useUserDispatch, useUserState } from "./user-manager-context";

const MAX_INDIVIDUAL_RESULTS = 20;

const UserInfoPanel = (props: ContainerLayoutProps) => {
  const { wide, autoFocus } = props;

  const { dataDetails, form = {} as UserProfileForm, errors = {} } = useUserState();
  const dispatch = useUserDispatch();

  const { getUserAssignment, checkUserEmail } = useUserApi();

  const isNew = !form.id;

  const colSize = wide ? 6 : 12;

  const [searchIndividual, { data: searchResult }] = useLazyQuery<ISearchIndividualsForNewUserResponse>(SEARCH_INDIVIDUALS_FOR_NEW_USER);
  const [getOrgByPosition, { loading: loadingOrg }] = useLazyQuery<{ orgByPosition: NamedEntity }>(GET_ORG_BY_POSITION);

  useEffect(() => {
    if (form.assignment?.position && !form.assignment.org) {
      getOrgByPosition(
        {
          variables: {
            positionId: form.assignment.position.id
          }
        })
        .then(response => dispatch(new Action(DataEntryEvent.UpdateForm,
          {
            assignment: {
              ...form.assignment,
              org: response.data?.orgByPosition
            }
          }
        )));
    }
  }, [form.assignment?.position]);

 
  const handleSearch = (name: string) => searchIndividual({
    variables: {
      input: {
        individualName: trimToEmpty(name),
        limit: MAX_INDIVIDUAL_RESULTS
      },
      isActive: true, // search individuals with active assignments only
      individualId: dataDetails?.individualId ?? 0 // if editing an existing OMS user, send the individial id. If adding a new OMS user, send a 0
    },fetchPolicy: 'network-only'
  });

  const individualOptions = useMemo(() => {
    if (!searchResult) return undefined;

    const options: IOption[] = [];

    // only return the same individual for updating
    searchResult.searchIndividualsForNewUser?.individuals?.filter(ind => !dataDetails?.individualId || ind.individualId === dataDetails?.individualId)
      .forEach((ind: ISearchIndividualsForNewResult) => {
        const assignments = ind.assignments?.filter(a => a.isActive) ?? [];
        if (assignments.length > 0) {
          assignments.forEach(assignment => {
            options.push({
              id: `${ind.individualId}_${assignment.assignmentId}`,
              label: ind.fullName,
              displayItem: <OmsToolTip positionId={assignment.positionId}>
                {ind.fullName}, {assignment.fullPositionName} ({assignment.relationshipType}), {ind.primaryEmail ?? 'N/A'}
              </OmsToolTip>
            });
          });
        } else {
          options.push({
            id: ind.individualId,
            label: ind.fullName,
            displayItem: `${ind.fullName}, ${ind.primaryEmail ?? 'N/A'}`
          });
        }
      });

    if (options?.length === MAX_INDIVIDUAL_RESULTS) {
      options.push({
        id: 0,
        label: 'Refine search keywords for more results...'
      });
    }

    return options;
  }, [searchResult, dataDetails?.individualId, isNew]);

  const handleConfirm = (option: IOption) => {
    const idInfo = option.id.toString().split('_');
    const indId = idInfo[0];
    const ind = searchResult?.searchIndividualsForNewUser?.individuals?.find(i => equals(i.individualId, indId));
    if (!ind) {
      console.error('Individual not found: %d', indId);
      return;
    }

    dispatch(new Action(DataEntryEvent.UpdateForm,
      {
        individualId: ind.individualId,
        email: ind.primaryEmail,
        individualName: option.label,
        assignment: undefined
      }
    ));

    // load assignment info
    const assignmentId = idInfo.length > 1 ? +idInfo[1] : undefined;
    if (assignmentId) {
      getUserAssignment(assignmentId)
        .then(assignment =>
          dispatch(new Action(DataEntryEvent.UpdateForm, { assignment: assignment }))
        );
    }

    if (!ind.primaryEmail) {
      dispatch(new Action(DataEntryEvent.AddErrors,
        { email: "User doesn't have an email." }
      ));
    } else if (isNew) {
      checkUserEmail(ind.primaryEmail);
    }
  };

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'individualName') {
      dispatch(new Action(DataEntryEvent.UpdateForm,
        { individualId: value.trim().toLowerCase() === dataDetails?.individualName?.toLowerCase() ? dataDetails.individualId : -1 } // indicates that individual is being changed
      ));
    }

    dispatch(new FormUpdateAction(event));
  }, [dataDetails]);

  const handleInvalid = (event: InvalidEvent<HTMLInputElement>) => {
    event.preventDefault();
    dispatch(new FormErrorAction(event));
  };

  return (
    <section>
      <Row className="gx-5">
        <Col md={colSize}>
          <FormGroup row>
            <Label for="individual_info" sm={4}>Name:</Label>
            <Col sm={8}>
              <SearchInput 
                auto 
                id="individual_info" 
                name="individualName"
                placeholder="Enter a name and search"
                value={form.individualName ?? ''} 
                onChange={handleChange}
                required
                invalid={!!errors.individualName} 
                onInvalid={handleInvalid}
                options={individualOptions}
                onSearch={handleSearch}
                onConfirm={handleConfirm}
                autoFocus={autoFocus} />
              <InlineFeedback invalid={!!errors.individualName}>
                {errors.individualName}
              </InlineFeedback>
            </Col>
          </FormGroup>
        </Col>
        {(!isNew || !!form.individualId) &&
          <Col md={colSize}>
            <FormGroup row>
              <Label for="email" sm={4}>Email:</Label>
              <Col sm={8}>
                <Input type="email" id="email" name="email"
                  value={form.email ?? ''} onChange={handleChange}
                  required
                  invalid={!!errors.email} onInvalid={handleInvalid} />
                <FormFeedback>{errors.email}</FormFeedback>
              </Col>
            </FormGroup>
          </Col>
        }
      </Row>

      {(!isNew || (!!form.individualId && form.assignment)) &&
        <Row className="gx-5">
          <Col md={colSize}>
            <FormGroup row>
              <LabelCol sm={4}>Organization:</LabelCol>
              <LabelCol sm={8}>
                <AnimatedPlaceholder color="secondary" xs={8} loading={loadingOrg}>
                  {form.assignment?.org &&
                    <OmsToolTip orgId={form.assignment?.org?.id}>{form.assignment?.org?.name}</OmsToolTip>
                  }
                </AnimatedPlaceholder>
              </LabelCol>
            </FormGroup>
          </Col>
          <Col md={colSize}>
            <FormGroup row>
              <LabelCol sm={4}>Position:</LabelCol>
              <LabelCol sm={8}>
                {!!form.assignment && (form.assignment.expired
                  ? <>{form.assignment?.position?.name} <FaTriangleExclamation className="mb-1 text-warning" title='Inactive assignment' /></>
                  : <OmsToolTip positionId={form.assignment?.position?.id}>{form.assignment?.position?.name}</OmsToolTip>
                )}
              </LabelCol>
            </FormGroup>
          </Col>
        </Row>
      }

      <Row>
        <Col>
          <FormGroup check>
            <Input type="checkbox" id="user_expired" name="expired"
              checked={form.expired} onChange={handleChange} />
            {' '}
            <Label for="user_expired" check>Disabled</Label>
          </FormGroup>
        </Col>
      </Row>
    </section>
  );
};

export default UserInfoPanel;
